import * as Sentry from "@sentry/browser";
import { Auth } from "../application/domain/auth/auth";
import tenantManagementClient from "../application/apis/tenant-management/tenant-management-client";
import { supplementOrgProductNames } from "../application/domain/organization/org-product";
import Analytics from "../common/analytics";
import apiClient from "../application/apis/api-client";

export class AuthUtil {
  // Endpoint must be pointed to https://github.com/graffer-inc/go-services/blob/develop/accounts/src/di/route.go#L84
  static logoutEndpoint = "";

  static configure(logout: string) {
    AuthUtil.logoutEndpoint = logout;
  }

  static async checkAuth(): Promise<Auth> {
    try {
      const { status, data } =
        await tenantManagementClient.checkAuthDashboard();
      if (data.isAuthed) {
        Sentry.setUser({
          user: {
            id: data.user.id,
            accountType: data.user.accountType,
          },
        });

        Analytics.configUser({
          userId: data.user.id,
          accountType: data.user.accountType,
        });

        if (data.user.accountType === "GrafferStaffAccount") {
          return Auth.serviceProviderAuth(status, data.isAuthed, data.user);
        }

        const org = await this.fetchOrganization(data.user.orgId);
        Analytics.configOrg({
          orgName: org.name,
          accountType: data.user.accountType,
        });

        return new Auth({
          status: status,
          isAuthed: data.isAuthed,
          data: {
            ...data.user,
            orgSlug: org.slug,
            orgName: org.name,
            groupDelegatedPermissions:
              data.user.groupDelegatedPermissions || {},
            orgProducts:
              data.user.orgProducts?.map(supplementOrgProductNames) || [],
          },
          lastChecked: new Date(),
        });
      } else {
        return new Auth({
          status: status,
          isAuthed: data.isAuthed,
          data: undefined,
          lastChecked: new Date(),
        });
      }
    } catch (e) {
      Sentry.captureException(e);
      return new Auth({
        error: e,
        isAuthed: false,
        lastChecked: new Date(),
      });
    }
  }

  static async logout() {
    await fetch(AuthUtil.logoutEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    return AuthUtil.checkAuth();
  }

  static toAdminLogin() {
    window.location.href = "/service-provider/login";
  }

  private static async fetchOrganization(orgId: string) {
    const { data: org } = await apiClient.getOrganization(orgId);
    return org;
  }
}
